import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { GraduationCapIcon, UsersIcon, ShieldCheckIcon, AwardIcon, CheckCircle2Icon, CalendarIcon, BookOpenIcon, MessageCircleIcon, TargetIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const tracks = [{
  icon: GraduationCapIcon,
  title: 'Educator Track',
  body: 'For teachers, tutors, and curriculum developers working with Muslim children and youth in non-Muslim settings.',
  items: ['Child development and safeguarding', 'Identity-aware pedagogy', 'Curriculum design for cultural sensitivity', 'Practical classroom strategies'],
  bg: 'bg-sage-100',
  to: '#teacher-certification'
}, {
  icon: UsersIcon,
  title: 'Community Leadership Track',
  body: 'For mentors, community organizers, and leaders supporting Muslim families and youth at the community level.',
  items: ['Ethical leadership principles', 'Community-based mentorship', 'Safeguarding and accountability', 'Family and youth support frameworks'],
  bg: 'bg-[#F7EDE9]',
  to: '#community-leadership'
}];
const standards = [{
  title: 'Safeguarding First',
  body: 'All programs follow rigorous safeguarding standards. Trust and protection are non-negotiable.'
}, {
  title: 'Ethical Practice',
  body: 'Practitioners operate with transparency, accountability, and respect for family autonomy.'
}, {
  title: 'Certified Completion',
  body: 'Certificates are issued upon successful completion where applicable — recognized within our network.'
}];
// Certification prices and tuition amounts are temporarily hidden. Set to true to restore them.
const SHOW_PRICES = false;
export function ProfessionalDevelopment() {
  return <>
      <SubpageHero eyebrow="Certification & leadership for educators and community builders" title="Professional Development" paragraphs={['Professional tracks designed for educators, mentors, and community leaders supporting Muslim families in non-Muslim countries.', 'Practical training, safeguarding standards, and ethical leadership — with certificates issued upon completion where applicable.']} primaryCta={{
      label: 'Explore Programs',
      to: '#programs'
    }} secondaryCta={{
      label: 'Request Info',
      to: '/en/contact'
    }} image="https://cdn.magicpatterns.com/uploads/mZ6s4PwoMdkaamZH9rLkBU/Prof-Dev.jpg" imageAlt="Muslim professional recording an educational discussion at a desk with a laptop and microphone" compact masked data-id="element-2031" data-mp="93l6nj730kpe" />

      {/* Two professional tracks */}
      <section id="programs" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-2032" data-mp="1j2cukwc038my">
        <Container data-id="element-2033" data-mp="1n47rlomwezux">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2034" data-mp="1n3oimxf87lt1">
            Two professional tracks
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2035" data-mp="1x1ce5pknoxc9">
            Choose the track that matches your role and impact. Both tracks
            emphasize ethical practice, safeguarding, and real-world
            application.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-2036" data-mp="260psavjq6a3b">
            {tracks.map((t, i) => <motion.div key={t.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            y: -2
          }} className="rounded-2xl" data-id="element-2037" data-mp="2au24g677wof6" data-mpi={i}>
                <Link to={t.to} className={`group block h-full rounded-2xl ${t.bg} border border-ink-900/5 p-7 shadow-soft transition-shadow hover:shadow-card`} data-id="element-2038" data-mp="3r8d7yaijm3i8">
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white border border-forest-700/10 text-forest-700" data-id="element-2039" data-mp="2yk966033eakt">
                    <t.icon size={18} strokeWidth={1.6} data-id="element-2040" data-mp="3ch4h5y2wmc3i" />
                  </span>
                  <h3 className="mt-5 font-display text-xl text-ink-900" data-id="element-2041" data-mp="38gc3msn01l1f">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-ink-700 leading-relaxed" data-id="element-2042" data-mp="nrenhzb6wd38">{t.body}</p>
                  <ul className="mt-5 space-y-2.5" data-id="element-2043" data-mp="nthb6covdu4s">
                    {t.items.map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700 text-sm" data-id="element-2044" data-mp="vd0cw05w8c2p">
                        <CheckCircle2Icon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={1.8} data-id="element-2045" data-mp="le1dl870pssd" />
                        {item}
                      </li>)}
                  </ul>
                </Link>
              </motion.div>)}
          </div>

          <div className="mt-8 flex flex-col gap-2 rounded-2xl border border-sage-200 bg-white px-6 py-5 sm:flex-row sm:items-center" data-id="element-2046" data-mp="14au5p14g8nl4">
            <span className="inline-flex w-fit whitespace-nowrap rounded-full bg-sage-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700" data-id="element-2047" data-mp="198c6234ehuhi">
              Coming soon
            </span>
            <p className="flex-1 text-center text-sm font-semibold text-ink-700" data-id="element-2048" data-mp="2bmzwfxal4bm5">
              The names of our confirmed teachers, educators, and Shariah Board
              members will be announced soon.
            </p>
          </div>
        </Container>
      </section>

      {/* Teacher Certification */}
      <section id="teacher-certification" className="relative py-16 md:py-24 overflow-hidden isolate scroll-mt-24 bg-white/60" data-id="element-2049" data-mp="1xngujm8d9wia">
        <PatternDecor side="right" y="top" size="lg" data-id="element-2050" data-mp="1spf2ibsyqmuo" />
        <Container data-id="element-2051" data-mp="1nwvt7r1b6dvo">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-2052" data-mp="1nwlyjq5bjijn">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-2053" data-mp="1iw82l6gzrz1z">
              <div data-id="element-2054" data-mp="19xw3eg227sxx">
                <div className="flex items-center gap-3 mb-3" data-id="element-2055" data-mp="19wb98rjnqc0l">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-2056" data-mp="14xzxgb9sxrfq">
                    12 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-2057" data-mp="3lk6lx9q2iwgk">
                    Certification
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-2058" data-mp="3qhyd21iw2ejp">
                  Teacher Certification
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-2059" data-mp="2xwgcl8zqk4w2">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-2060" data-mp="32vjiswrgch5m">
                  €590{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-2061" data-mp="36v0onrasjgbb">
                    / certification
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-2062" data-mp="2f55mgu1u3w58">
                  <Button variant="primary" data-id="element-2063" data-mp="2f44amnczv5mg">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-2064" data-mp="2j4eh014p7y1u">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-2065" data-mp="1cl9ebkxrspq4">
                {/* Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-2066" data-mp="1mi4ahx6kme18">
                  Become a confident, certified educator — equipped to teach
                  Muslim children with care, safeguarding, and ethical,
                  identity-aware methods.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2067" data-mp="1qi6faf7lrnkq">
                  A structured online certification for educators supporting
                  Muslim children in non-Muslim countries, grounded in child
                  development, ethical teaching standards, and safeguarding.
                </p>

                {/* Who it's for (Chips) */}
                <div className="mt-10" data-id="element-2068" data-mp="2a76ydmrst346">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2069" data-mp="2a8i5nwkgct01">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2070" data-mp="2xukemr2tvur5">
                    {['Teachers and educators', 'Homeschool parents', 'Youth mentors', 'Community workers'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-2071" data-mp="2a47wyohs6jnd">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* What's included (Icon row) */}
                <div className="mt-10" data-id="element-2072" data-mp="1r5lup6aj2mhv">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2073" data-mp="1r6ni8a8qd1gh">
                    What's included
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-2074" data-mp="1zevzdz1y7td2">
                    {[{
                    icon: BookOpenIcon,
                    text: 'Structured online delivery'
                  }, {
                    icon: CheckCircle2Icon,
                    text: 'Ethical teaching standards'
                  }, {
                    icon: ShieldCheckIcon,
                    text: 'Safeguarding & responsibility'
                  }, {
                    icon: AwardIcon,
                    text: 'Certificate of completion'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-2075" data-mp="2ddnb5etc92y1">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-2076" data-mp="313q37ux968wn">
                          <f.icon size={14} strokeWidth={2} data-id="element-2077" data-mp="uh25pwvbdpxd" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-2078" data-mp="uepib46sencx">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-2079" data-mp="z7qw18v9cgux">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-2080" data-mp="za4s9l1obz42">
                    Where you'll be in 12 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-2081" data-mp="6k4vyal8nuct">
                    {['A certification recognized within our partner network', 'Confidence in safeguarding & ethics', 'Identity-aware teaching methods', 'Practical online classroom skills'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-2082" data-mp="1qyyd5w30u0jz">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-2083" data-mp="onmqxu04xpcs" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Curriculum & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-2084" data-mp="9wm76stxu91c">
                <div className="px-7 md:px-10 py-10" data-id="element-2085" data-mp="9x5pwbrex1gf">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-2086" data-mp="evz2m1yvkofj">
                    Course Curriculum
                  </h4>

                  {/* Curriculum list */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-2087" data-mp="3id7lmp5c9ubu">
                    {[{
                    num: '1',
                    title: 'Educational Psychology & Child Development',
                    desc: 'Cognitive, emotional, and social development stages in Muslim children'
                  }, {
                    num: '2',
                    title: 'Foundations of Tarbiyah & Ethics',
                    desc: "Islamic educational philosophy, prophetic methodology, and teacher's adab"
                  }, {
                    num: '3',
                    title: 'Modern Pedagogy & Teaching Methodologies',
                    desc: 'Active learning, inquiry-based instruction, and critical thinking tools'
                  }, {
                    num: '4',
                    title: 'Inclusive Education & Diverse Learning Needs',
                    desc: 'Identifying learning barriers, differentiation strategies, and supportive adjustments'
                  }, {
                    num: '5',
                    title: 'Curriculum Mapping & Syllabus Design',
                    desc: 'Aligning educational standards with core values and structured learning paths'
                  }, {
                    num: '6',
                    title: 'Lesson Planning & Resource Development',
                    desc: 'Creating actionable, engaging lesson plans and selecting ethical materials'
                  }, {
                    num: '7',
                    title: 'Classroom Management & Positive Discipline',
                    desc: 'Fostering a respectful learning culture, behavior intervention, and motivation'
                  }, {
                    num: '8',
                    title: 'Safeguarding, Child Protection & Legal Duty',
                    desc: 'Child safety regulations, identifying risks, compliance, and institutional policy'
                  }, {
                    num: '9',
                    title: 'Educational Technology & Digital Literacy',
                    desc: 'Utilizing modern digital tools safely and effectively in the learning process'
                  }, {
                    num: '10',
                    title: 'Student Assessment & Progress Tracking',
                    desc: 'Formative and summative evaluation, grading criteria, and constructive feedback'
                  }, {
                    num: '11',
                    title: 'Parent-Teacher Collaboration & Communication',
                    desc: 'Building trust with families, conducting conferences, and reporting progress'
                  }, {
                    num: '12',
                    title: 'Professional Practicum & Portfolio Defense',
                    desc: 'Final teaching demonstration, portfolio review, and certification award'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-2088" data-mp="2w7khbpowufp1">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-2089" data-mp="2w9dpxaj339zo">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-2090" data-mp="3a5r52d05azaz">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-2091" data-mp="3jxer1ydxno52">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-2092" data-mp="3sx1v6ry1r81t">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-2093" data-mp="1h83ovu2xi728">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-2094" data-mp="18al0cfq2tall">
                      <span className="font-display text-3xl text-ink-900" data-id="element-2095" data-mp="13aowqhlajl6k">
                        €590
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-2096" data-mp="20ww0xqdonbie">
                        / certification
                      </span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-2097" data-mp="1r72zhi798crv">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2098" data-mp="1m9kozndl9phr">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-2099" data-mp="1m7gkp4bs2ogy" />
                        <span data-id="element-2100" data-mp="2jpzyvnxxoait">Certificate upon completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2101" data-mp="2ewdwqppebp19">
                        <UsersIcon size={14} className="text-forest-700" data-id="element-2102" data-mp="1n9lxhnz5o5f3" />
                        <span data-id="element-2103" data-mp="1s1dou0q4yf6a">Small, dedicated cohorts</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-2104" data-mp="1e9z1xit3xtty">
                        <ShieldCheckIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-2105" data-mp="1iban9c4y7swc" />
                        <span data-id="element-2106" data-mp="25ysgzd7rx11y">
                          Limited scholarships via community partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-2107" data-mp="1wzxdwa01072v">
                      <Button variant="primary" className="w-full justify-center" data-id="element-2108" data-mp="21y8mkhel4bji">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Community Leadership */}
      <section id="community-leadership" className="py-16 md:py-24 bg-sand-50/60 scroll-mt-24" data-id="element-2109" data-mp="e0zefgiyyt8s">
        <Container data-id="element-2110" data-mp="rz7ie8efautg">
          <div className="rounded-3xl bg-white border border-sage-200 shadow-soft overflow-hidden" data-id="element-2111" data-mp="n27bxwdnvq1t">
            {/* Header Band */}
            <div className="px-7 md:px-10 py-8 bg-sage-50 border-b border-sage-200 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-2112" data-mp="3vh8uitqxarpf">
              <div data-id="element-2113" data-mp="3qh30x813ev4p">
                <div className="flex items-center gap-3 mb-3" data-id="element-2114" data-mp="9260xo3on28m">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold tracking-wide" data-id="element-2115" data-mp="93qw9fxtmp7j">
                    10 Weeks
                  </span>
                  <span className="text-ink-500 text-sm font-medium" data-id="element-2116" data-mp="3cpg1x0frn8i8">
                    Certification
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-ink-900" data-id="element-2117" data-mp="247gt44nf9umi">
                  Community Leadership
                </h2>
              </div>
              <div className="flex flex-col md:items-end gap-3" data-id="element-2118" data-mp="1z7c944nb2hrj">
                {SHOW_PRICES && <p className="text-2xl font-display text-forest-700" data-id="element-2119" data-mp="2dzwbh32msl7x">
                  €490{' '}
                  <span className="text-base text-ink-500 font-sans" data-id="element-2120" data-mp="295i7wddtf7s6">
                    / certification
                  </span>
                </p>}
                <Link to="/waitlist" data-id="element-2121" data-mp="2hz4zrsewjqw3">
                  <Button variant="primary" data-id="element-2122" data-mp="2wwxcydwz8374">Reserve your seat</Button>
                </Link>
              </div>
            </div>

            <div className="grid lg:grid-cols-12 gap-0" data-id="element-2123" data-mp="2rxkue5d51iac">
              {/* Left Column: Content */}
              <div className="lg:col-span-7 px-7 md:px-10 py-10 border-b lg:border-b-0 lg:border-r border-sage-200" data-id="element-2124" data-mp="12i4oqzyfaep7">
                {/* Outcome-first framing */}
                <h3 className="text-xl md:text-2xl font-display text-ink-900 leading-snug" data-id="element-2125" data-mp="3976k1emfenzq">
                  Lead your community with confidence — equipped with ethical
                  frameworks, communication skills, and a real, actionable
                  project plan.
                </h3>
                <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2126" data-mp="34gghe95dwz8q">
                  A certification for community leaders and coordinators,
                  blending Islamic ethical leadership with practical
                  communication, conflict resolution, and community project
                  design.
                </p>

                {/* Who it's for (Chips) */}
                <div className="mt-10" data-id="element-2127" data-mp="2gklvelxc4pzv">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2128" data-mp="2lh2h79h2as9s">
                    Who this is for
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-2129" data-mp="epbhw56f18y6">
                    {['Community leaders & coordinators', 'Imams & assistants (community role)', "Youth & women's group coordinators", 'Active volunteers & mentors'].map(chip => <span key={chip} className="inline-flex items-center px-3 py-1.5 rounded-lg bg-sand-50 border border-sand-200 text-ink-700 text-sm" data-id="element-2130" data-mp="8bxgosu1qiyu">
                        {chip}
                      </span>)}
                  </div>
                </div>

                {/* What's included (Icon row) */}
                <div className="mt-10" data-id="element-2131" data-mp="r8fhdzqvht78">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-2132" data-mp="r7dvf08bjmaq">
                    What's included
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-2133" data-mp="1jrm3nk7avhzn">
                    {[{
                    icon: TargetIcon,
                    text: 'Leadership frameworks with Islamic ethics'
                  }, {
                    icon: MessageCircleIcon,
                    text: 'Communication & conflict tools'
                  }, {
                    icon: BookOpenIcon,
                    text: 'A practical community project plan'
                  }, {
                    icon: AwardIcon,
                    text: 'Certificate of completion'
                  }].map(f => <div key={f.text} className="flex items-center gap-3" data-id="element-2134" data-mp="2eymsur0k2buq">
                        <span className="w-8 h-8 rounded-full bg-sage-100 text-forest-700 flex items-center justify-center flex-shrink-0" data-id="element-2135" data-mp="2jy80dptsjsj2">
                          <f.icon size={14} strokeWidth={2} data-id="element-2136" data-mp="2oydlen5k2gs6" />
                        </span>
                        <span className="text-sm text-ink-700" data-id="element-2137" data-mp="2tvm3knqpektf">{f.text}</span>
                      </div>)}
                  </div>
                </div>

                {/* Outcomes */}
                <div className="mt-10 bg-sage-50 rounded-2xl p-6 border border-sage-200" data-id="element-2138" data-mp="3lno7rpg8wqef">
                  <h4 className="font-display text-lg text-ink-900 mb-4" data-id="element-2139" data-mp="48heo92hua8f">
                    Where you'll be in 10 weeks
                  </h4>
                  <ul className="space-y-3" data-id="element-2140" data-mp="3vglsm28pmrg7">
                    {['An ethical leadership toolkit', 'Stronger communication & conflict skills', 'A ready-to-run community project', 'A certification recognized within our partner network'].map(item => <li key={item} className="flex items-start gap-3 text-ink-800 font-medium" data-id="element-2141" data-mp="3cn84afzi5j9k">
                        <CheckCircle2Icon size={18} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-2142" data-mp="ryboehriniog" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Right Column: Curriculum & Pricing */}
              <div className="lg:col-span-5 bg-[#FAFAFA]" data-id="element-2143" data-mp="uj9pi4pdlexe">
                <div className="px-7 md:px-10 py-10" data-id="element-2144" data-mp="1i7svueov7hmu">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-6" data-id="element-2145" data-mp="1i8b2m595le90">
                    Course Curriculum
                  </h4>

                  {/* Curriculum list */}
                  <div className="relative border-l border-sage-200 ml-3 space-y-8 pb-4" data-id="element-2146" data-mp="18hpvtujbt3f0">
                    {[{
                    num: '1',
                    title: 'Prophetic Leadership & Servant Model',
                    desc: 'Ethical frameworks, accountability, and the concept of Amanah (trust)'
                  }, {
                    num: '2',
                    title: 'Strategic Community Needs Assessment',
                    desc: 'Demographic analysis, mapping local infrastructure, and identifying systemic gaps'
                  }, {
                    num: '3',
                    title: 'Advanced Communication & Public Relations',
                    desc: 'Public speaking, media engagement, crisis communication, and cross-cultural dialogue'
                  }, {
                    num: '4',
                    title: 'Project Design & Operational Planning',
                    desc: 'Transforming community needs into structured, executable, and measurable project plans'
                  }, {
                    num: '5',
                    title: 'Safeguarding, Ethics & Volunteer Management',
                    desc: 'Ensuring boundaries, safety protocols, and effective coordination of community teams'
                  }, {
                    num: '6',
                    title: 'Financial Stewardship & Ethical Fundraising',
                    desc: 'Grant writing, crowdfunding, halal budgeting, and transparent resource allocation'
                  }, {
                    num: '7',
                    title: 'Conflict Resolution & Community Mediation',
                    desc: 'Navigating internal friction, balancing diverse stakeholders, and consensus-building'
                  }, {
                    num: '8',
                    title: 'Legal Compliance & Western Regulations',
                    desc: 'Governance structures, NGO/charity laws, compliance, and risk management'
                  }, {
                    num: '9',
                    title: 'Strategic Marketing & Community Mobilization',
                    desc: 'Building awareness, digital outreach, and driving sustainable public engagement'
                  }, {
                    num: '10',
                    title: 'Project Launch & Long-Term Sustainability',
                    desc: 'Executing the capstone project, impact evaluation, and strategic scaling'
                  }].map(subject => <div key={subject.num} className="relative pl-6" data-id="element-2147" data-mp="1uoajyqmljm22">
                        <span className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-white border-2 border-forest-700 flex items-center justify-center text-[10px] font-bold text-forest-700" data-id="element-2148" data-mp="29erhw000vtu7">
                          {subject.num}
                        </span>
                        <h5 className="font-display text-lg text-ink-900 leading-none mb-2 pt-1" data-id="element-2149" data-mp="2icjyr8le79jp">
                          {subject.title}
                        </h5>
                        <p className="text-sm text-ink-600" data-id="element-2150" data-mp="2x24umubug3cf">{subject.desc}</p>
                      </div>)}
                  </div>

                  {/* Pricing Module */}
                  <div className="mt-10 rounded-2xl bg-white border border-sage-200 p-6 shadow-sm" data-id="element-2151" data-mp="2o05dm20twz9h">
                    {SHOW_PRICES && <>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2" data-id="element-2152" data-mp="2ebdo00j8qr1q">
                      Tuition
                    </p>
                    <div className="flex items-baseline gap-2 mb-4" data-id="element-2153" data-mp="2j3x2czpr5q7l">
                      <span className="font-display text-3xl text-ink-900" data-id="element-2154" data-mp="25bqltcioth7m">
                        €490
                      </span>
                      <span className="text-ink-500 text-sm" data-id="element-2155" data-mp="299y5og8g6yn1">
                        / certification
                      </span>
                    </div>
                    </>}

                    <div className="space-y-3 mb-6" data-id="element-2156" data-mp="20d6z39w9s1e7">
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2157" data-mp="1xogqfn9rjhym">
                        <AwardIcon size={14} className="text-forest-700" data-id="element-2158" data-mp="22lqdwxdldjjt" />
                        <span data-id="element-2159" data-mp="26o3l7q7zlpip">Certificate upon completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-700" data-id="element-2160" data-mp="2bn6nx40hvjni">
                        <TargetIcon size={14} className="text-forest-700" data-id="element-2161" data-mp="2bl3ra14u6s9t" />
                        <span data-id="element-2162" data-mp="2glroj4jns5ch">Real community project plan</span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-ink-700" data-id="element-2163" data-mp="2lk2xld24qbtn">
                        <UsersIcon size={14} className="text-forest-700 mt-0.5 flex-shrink-0" data-id="element-2164" data-mp="2qd5iyxwr6eyr" />
                        <span data-id="element-2165" data-mp="2zd10rlktd87r">
                          Limited scholarships via community partnerships
                        </span>
                      </div>
                    </div>

                    <Link to="/waitlist" className="block w-full" data-id="element-2166" data-mp="3efspamcuqts7">
                      <Button variant="primary" className="w-full justify-center" data-id="element-2167" data-mp="3eh3tuoe4g5kd">
                        Reserve your seat
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Standards */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-2168" data-mp="130dvmr197bw">
        <PatternDecor side="left" y="middle" size="md" data-id="element-2169" data-mp="3s7z3i2wg2qp0" />
        <Container data-id="element-2170" data-mp="3nfp6y40tqsdr">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2171" data-mp="3nf5zjh8svmdz">
            Standards & certification
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-2172" data-mp="3jd2kkimx9zny">
            Our professional programs are built around three commitments:
            safeguarding, ethical practice, and recognized completion.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5" data-id="element-2173" data-mp="298sv1smdyrp0">
            {standards.map((s, i) => {
            const tones = ['bg-sage-100', 'bg-[#EDF2F7]', 'bg-[#F7EDE9]'];
            return <motion.div key={s.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className={`rounded-2xl ${tones[i % tones.length]} border border-ink-900/5 p-6 shadow-soft`} data-id="element-2174" data-mp="1zizlex5t0mne" data-mpi={i}>
                  <span className="w-11 h-11 inline-flex items-center justify-center rounded-xl bg-white text-forest-700" data-id="element-2175" data-mp="3ayo3pb844xhv">
                    {i === 0 ? <ShieldCheckIcon size={18} strokeWidth={1.6} data-id="element-2176" data-mp="148unqgs1wavu" /> : i === 1 ? <CheckCircle2Icon size={18} strokeWidth={1.6} data-id="element-2177" data-mp="zbko68ay6iua" /> : <AwardIcon size={18} strokeWidth={1.6} data-id="element-2178" data-mp="1dzpbxqouxkjx" />}
                  </span>
                  <h3 className="mt-5 font-display text-lg text-ink-900" data-id="element-2179" data-mp="197nxby47vhqq">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-2180" data-mp="1myapf7fh29du">
                    {s.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </Container>
      </section>

      {/* Admission & Scholarships */}
      <section className="py-16 md:py-24 bg-[#EDF2F7]/60" data-id="element-2181" data-mp="1s08be9f9biah">
        <Container data-id="element-2182" data-mp="2k0l2o8hg1du">
          <h3 className="font-display text-lg text-ink-900" data-id="element-2183" data-mp="2jh2r6ex7aza">
            Admission & Scholarships
          </h3>
          <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-2184" data-mp="3trlgozl4zs72">
            Some cohorts may include limited scholarship places through
            community partnerships. Certification tracks may include completion
            requirements and assessment.
          </p>
        </Container>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-sage-50/60" data-id="element-2185" data-mp="9122xz577jah">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-2186" data-mp="dyakpymfplb7" />
        <Container data-id="element-2187" data-mp="3chhhjs06ebrt">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-2188" data-mp="3ciaiudgd892k">
            <div className="max-w-xl" data-id="element-2189" data-mp="3hfj4io0q2v8k">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-2190" data-mp="3hglrikuxcdj7">
                Level up your impact
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-2191" data-mp="3r6nj3mmhke2v">
                Request details and choose the right professional track for your
                role.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2192" data-mp="2ypkwg7wxd24x">
              <Link to="/en/waitlist" data-id="element-2193" data-mp="2yo9u8cj4o2fx">
                <Button variant="primary" data-id="element-2194" data-mp="1fuq35x7k93gm">Join</Button>
              </Link>
              <Link to="/en/contact" data-id="element-2195" data-mp="1fsm35qcs8olm">
                <Button variant="secondary" data-id="element-2196" data-mp="1bsle7bs24v0b">Request Info</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}